import { useState } from 'react';
import { all, SetExceptionsHidden } from '@/api/dex360-audit';
import { Button } from '@/components/ui/button';
import { DateRangePickerSplit } from '@/components/ui/date-range-picker-split';
import { SearchBar } from '@/components/ui/SearchBar/SearchBar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { toast } from 'sonner';

import { Dex360AuditTable } from './table';

export function Dex360AuditTabs() {
	const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
	const [globalSearch, setGlobalSearch] = useState('');
	const [tabSearch, setTabSearch] = useState('');
	const [startDate, setStartDate] = useState<Date | null>(null);
	const [endDate, setEndDate] = useState<Date | null>(null);
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});
	const [sorting, setSorting] = useState<SortingState>([
		{ id: 'errorDate', desc: true },
	]);
	const [currentTab, setCurrentTab] = useState<'active' | 'hidden' | 'resolved'>(
		'active',
	);
	
	const queryClient = useQueryClient();
	const { data, isLoading, isFetching } = useQuery({
		queryKey: [
			'dex360-audit',
			globalSearch,
			currentTab,
			tabSearch,
			startDate,
			endDate,
			pagination,
			sorting,
		],
		queryFn: () =>
			all(
				globalSearch,
				currentTab,
				tabSearch,
				startDate,
				endDate,
				sorting,
				pagination,
			),
		placeholderData: keepPreviousData,
		staleTime: 30000,
	});

	const mutation = useMutation({
		mutationFn: ({ ids, hide }: { ids: number[]; hide: boolean }) =>
			SetExceptionsHidden(ids, hide),
		onSuccess: async (_, { hide }) => {
			await queryClient.invalidateQueries({ queryKey: ['dex360-audit'] });
			setRowSelection({});
			toast.success(
				hide
					? 'Successfully hid selected exceptions.'
					: 'Successfully unhid selected exceptions.',
			);
		},
		onError: (_, { hide }) => {
			toast.error(
				hide ? 'Failed to hide exceptions.' : 'Failed to unhide exceptions.',
			);
		},
	});

	const handleHideToggle = (selectedIds: number[]) => {
		if (selectedIds.length === 0) return;
		const hide = currentTab === 'active';
		mutation.mutate({ ids: selectedIds, hide });
	};

	const handleSingleHide = (id: number) => {
		mutation.mutate({ ids: [id], hide: true });
	};

	const selectedIds = Object.entries(rowSelection)
		.filter(([_, selected]) => selected)
		.map(([id]) => Number(id));
	
	return (
		<>
			<div className="mb-4 flex justify-center">
				<SearchBar
					placeholder="Search..."
					onSearch={(searchTerm) => {
						setGlobalSearch(searchTerm);
						setPagination((prev) => ({
							...prev,
							pageIndex: 0,
						}));
					}}
					isLoading={isLoading}
				/>
			</div>
			<Tabs
				value={currentTab}
				onValueChange={(val) => {
					setCurrentTab(val as any);
					setRowSelection({});
					setTabSearch('');
					setStartDate(null);
					setEndDate(null);
					setPagination((prev) => ({
						...prev,
						pageIndex: 0,
					}));
				}}
				className="w-full"
			>
				<TabsList className="mb-4">
					<TabsTrigger value="active">Active Errors</TabsTrigger>
					<TabsTrigger value="hidden">Hidden</TabsTrigger>
					<TabsTrigger value="resolved">Resolved</TabsTrigger>
				</TabsList>

				<TabsContent value={currentTab}>
					<div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
						<div className="flex flex-col md:flex-row md:items-center md:gap-4">
							<SearchBar
								placeholder={`Search ${currentTab} tab...`}
								onSearch={(searchTerm) => {
									setTabSearch(searchTerm);
									setPagination((prev) => ({
										...prev,
										pageIndex: 0,
									}));
								}}
								isLoading={isLoading}
								className="w-52"
								value={tabSearch}
							/>
							<div className="flex items-center gap-2">
								<DateRangePickerSplit
									startDate={startDate}
									endDate={endDate}
									onChange={(start, end) => {
										setStartDate(start);
										setEndDate(end);
										setPagination((prev) => ({
											...prev,
											pageIndex: 0,
										}));
									}}
								/>
								<Button
									variant="outline"
									onClick={() => {
										setStartDate(null);
										setEndDate(null);
										setPagination((prev) => ({
											...prev,
											pageIndex: 0,
										}));
									}}
									disabled={!startDate && !endDate}
								>
									Clear Date
								</Button>
							</div>
						</div>

						{(currentTab === 'active' || currentTab === 'hidden') && (
							<Button
								onClick={() => handleHideToggle(selectedIds)}
								disabled={selectedIds.length === 0}
								className="self-end md:self-auto"
							>
								{currentTab === 'active' ? 'Hide Selected' : 'Unhide Selected'}
							</Button>
						)}
					</div>

					<Dex360AuditTable
						data={data?.data || []}
						totalCount={data?.total || 0}
						pagination={pagination}
						setPagination={setPagination}
						sorting={sorting}
						setSorting={setSorting}
						isActiveTab={currentTab === 'active'}
						rowSelection={rowSelection}
						setRowSelection={setRowSelection}
						onSingleHide={handleSingleHide}
						isFetching={isFetching}
					/>
				</TabsContent>
			</Tabs>
		</>
	);
}
